import { useCallback, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';

const saveAsFile = (href: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getFileNameFromUrl = (url: string, fallbackName: string) => {
  try {
    const name = new URL(url, window.location.origin).pathname.split('/').pop();
    if (name) return decodeURIComponent(name);
  } catch {
    // fall through to the fallback name
  }
  return fallbackName;
};

/**
 * Downloads a file as binary data so it is saved as a file — a cross-origin `<a download>` is ignored by
 * the browser and opens the file instead — and so the caller can render progress.
 */
const useFileDownload = () => {
  const { onShowAlert } = useEnqueueSnackbar();
  const [progress, setProgress] = useState<number | null>(null);

  const download = useCallback(
    async (url: string | undefined, fallbackName: string) => {
      if (!url || progress !== null) return;

      const fileName = getFileNameFromUrl(url, fallbackName);
      setProgress(0);

      const fetchAsBlob = async () => {
        // `no-cache` revalidates instead of reusing a locally cached copy, which can be one without CORS
        // headers stored by an `<img>` load — the request carries `cache-control: no-cache`
        const response = await fetch(url, { cache: 'no-cache' });
        if (!response.ok) throw new Error(`File request failed with status ${response.status}`);

        const contentType = response.headers.get('content-type') ?? 'application/octet-stream';
        const total = Number(response.headers.get('content-length')) || 0;
        const reader = response.body?.getReader();

        if (!reader) return response.blob();

        const chunks: BlobPart[] = [];
        let received = 0;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          if (total) setProgress(Math.min(100, Math.round((received / total) * 100)));
        }

        return new Blob(chunks, { type: contentType });
      };

      try {
        const blob = await fetchAsBlob();

        setProgress(100);

        const objectUrl = URL.createObjectURL(blob);
        saveAsFile(objectUrl, fileName);
        URL.revokeObjectURL(objectUrl);
        onShowAlert(Date.now(), `Successfully downloaded ${fileName}`);
      } catch {
        // CloudFront serves some assets from a cached copy that carries no CORS headers, so the blob read
        // is blocked until that copy expires — nothing the client can do about it
        enqueueSnackbar('This file is temporarily not downloadable (asset host CORS). Please try again later.', {
          variant: 'error',
        });
      } finally {
        setProgress(null);
      }
    },
    [progress, onShowAlert]
  );

  return { progress, download };
};

export default useFileDownload;
